import React, { useEffect, useState } from "react";
import { Button } from "../../../../../shadcn/components/ui/button";
import {
  getAuctionEventByUuid,
  getBidderByUuid,
  updateBidderForEvent,
} from "services";

interface NIDeclarationComponentProps {
  bidderUuid: any;
  init: any;
  bidder: any;
  activeStep: number;
  setActiveStep: any;
  outerNextStep?: any;
}

const normalizeConstitutionTypeForDeclaration = (value?: string): string => {
  if (!value) return "";
  const normalized = value.toLowerCase().trim();

  if (normalized === "llp") return "llpfirm";
  if (normalized === "partnership") return "partnershipfirm";
  if (normalized === "proprietorship" || normalized === "proprietor")
    return "proprietors";
  if (normalized === "association_of_persons") return "aop";
  if (normalized === "body_of_individuals") return "boi";
  if (normalized === "government" || normalized === "government_agency")
    return "govt";
  if (normalized === "local_authority") return "local";
  if (
    normalized === "artificial_judicial_person" ||
    normalized === "artificial_juridical_person"
  ) {
    return "ajp";
  }
  if (normalized === "society") return "aop";

  return normalized;
};

// Constitution type mapping
const constitutionTypeMap: Record<string, string> = {
  private_limited: "Private Limited Company",
  public_limited: "Public Limited Company",
  company: "Company",
  llpfirm: "Limited Liability Partnership (LLP)",
  partnershipfirm: "Partnership Firm",
  proprietors: "Proprietors",
  trust: "Trust",
  huf: "Hindu Undivided Family",
  aop: "Association of Persons",
  boi: "Body of Individuals",
  govt: "Government Agency",
  local: "Local Authority",
  ajp: "Artificial Juridical Person",
};

// Declaration text mapping - user can update these
const declarationTextMap: Record<string, string> = {
  private_limited:
    "We, the Board of Directors of <Company Name> Private Limited, hereby declare that the Company is duly incorporated and that the authorised signatory(ies) named herein are empowered by Board Resolution to participate in auctions and submit bids on behalf of the Company.",
  public_limited:
    "We, the Board of Directors of <Company Name> Limited, hereby declare that the Company is duly incorporated and that the authorised signatory(ies) named herein are empowered by Board Resolution to participate in auctions and submit bids on behalf of the Company.",
  company:
    "We, on behalf of <Company Name>, hereby declare that the Company is duly incorporated and that the authorised signatory(ies) named herein are empowered to participate in auctions and submit bids on behalf of the Company.",
  llpfirm:
    "We, designated partners of <LLP Name>, hereby declare that the LLP is validly existing and that the authorised signatory(ies) named herein are empowered to participate and submit bids on behalf of the LLP.",
  partnershipfirm:
    "We, the partners of <Firm Name>, hereby declare that the firm is duly constituted and that the authorised signatory(ies) named herein are empowered under the Partnership Deed / resolution to participate and submit bids on behalf of the firm.",
  proprietors:
    "I, <Name>, sole proprietor of <Firm Name>, hereby declare that I am the sole owner and fully authorised to participate and submit bids on behalf of the said concern.",
  trust:
    "We, the trustees of <Trust Name>, hereby declare that the Trust is validly constituted and that the authorised signatory(ies) named herein are empowered under the Trust Deed / resolution to participate and submit bids on behalf of the Trust.",
  huf: "I, <Name>, Karta of <HUF Name>, hereby declare that I am duly authorised to represent the HUF and submit bids on its behalf.",
  aop: "We, on behalf of <Society/Association/Club Name>, hereby declare that the entity is duly registered and that the authorised signatory(ies) named herein are empowered by resolution to participate and submit bids on its behalf.",
  boi: "We, on behalf of <Society/Association/Club Name>, hereby declare that the entity is duly constituted and that the authorised signatory(ies) named herein are empowered by resolution to participate and submit bids on its behalf.",
  govt:
    "We, on behalf of <Company Name>, hereby declare that the authorised signatory(ies) named herein are duly empowered by competent authority to participate and submit bids on behalf of the Government Agency.",
  local:
    "We, on behalf of <Company Name>, hereby declare that the authorised signatory(ies) named herein are duly empowered by competent authority to participate and submit bids on behalf of the Local Authority.",
  ajp: "We, on behalf of <Company Name>, hereby declare that the authorised signatory(ies) named herein are duly empowered to participate and submit bids on behalf of the Artificial Juridical Person.",
};

export default function NIDeclarationComponent({
  bidderUuid,
  init,
  bidder,
  activeStep,
  setActiveStep,
  outerNextStep = null,
}: NIDeclarationComponentProps) {
  const [isChecked, setIsChecked] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [eventData, setEventData] = useState<any>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data: any = await getAuctionEventByUuid(bidder?.event_uuid);
      setEventData(data);
      setLoading(false);
    })();
  }, [bidder]);

  const handleSubmit = async () => {
    if (!isChecked) {
      setError("You must confirm the declaration before proceeding.");
      return;
    }
    setLoading(true);
    const data: any = await getBidderByUuid(bidderUuid);
    const res5: any = await updateBidderForEvent({
      uuid: bidderUuid,
      meta: {
        ...data?.meta,
        steps_status: {
          ...data?.meta?.steps_status,
          [outerNextStep === null ? "declaration_of_ar" : outerNextStep]:
            "completed",
        },
      },
      verification_step: outerNextStep || "t_&_c",
      status: "pending",
    });
    setActiveStep(activeStep + 1);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setLoading(false);
    setError("");
  };

  // Get entity name
  const entityName = bidder?.meta?.name || "N/A";

  // Get authorized signatory name
  const authorizedSignatory =
    bidder?.application_form_data?.ar_details?.pan_name || "N/A";

  const constitutionTypeValue =
    bidder?.kyc_data?.document_details?.entity_type ||
    bidder?.application_form_data?.constitution_type ||
    bidder?.kyc_data?.gst_data?.details?.ctb;
  const normalizedConstitutionTypeValue =
    normalizeConstitutionTypeForDeclaration(constitutionTypeValue);
  let constitutionTypeLabel =
    constitutionTypeMap[normalizedConstitutionTypeValue] || "N/A";
  if (process.env.NEXT_PUBLIC_ENABLE_OFFLINE_NI_KYC !== "true") {
    constitutionTypeLabel = constitutionTypeValue || "N/A";
  }

  // Get declaration text based on constitution type and replace placeholders
  const getDeclarationText = () => {
    if (process.env.NEXT_PUBLIC_ENABLE_OFFLINE_NI_KYC !== "true") {
      return `RESOLVED THAT ${bidder?.application_form_data?.ar_details?.pan_name}, ${bidder?.application_form_data?.ar_details?.designation}  of  ${bidder?.meta?.name}, be and is hereby severally authorized to participate in the e-auction conducted by ${eventData?.meta?.bank_name}, ${eventData?.meta?.branch_name} dated ${bidder?.meta?.auction_start_datetime}, for the above-mentioned property, Reserve Price Rs ${eventData?.reserve_price} on behalf of the Company.`;
    }
    let text =
      declarationTextMap[normalizedConstitutionTypeValue] ||
      "Please select a constitution type to view the declaration.";

    // Replace all entity name placeholders with actual entity name
    text = text.replace(/<Company Name>/g, entityName);
    text = text.replace(/<Firm Name>/g, entityName);
    text = text.replace(/<LLP Name>/g, entityName);
    text = text.replace(/<Trust Name>/g, entityName);
    text = text.replace(/<HUF Name>/g, entityName);
    text = text.replace(/<Society\/Association\/Club Name>/g, entityName);

    // Replace <Name> placeholder with AR name
    text = text.replace(/<Name>/g, authorizedSignatory);

    return text;
  };

  const declarationText = getDeclarationText();

  // Get auction ID
  const auctionId = eventData?.event_id || bidder?.meta?.event_id || "N/A";

  return (
    <div className="font-openSans">
      {/* Declaration Header */}
      <h2 className="text-lg font-bold mb-2">Declaration</h2>
      <p className="text-sm text-gray-600 mb-6">
        Review the declaration as per constitution and confirm.
      </p>

      {/* Entity & Signatory Summary */}
      <div className="mb-6">
        <h3 className="text-sm font-bold mb-3">Entity & Signatory Summary</h3>
        <div className="space-y-2 text-sm">
          <div>
            <span className="font-semibold">Entity:</span>{" "}
            <span>{entityName}</span>
          </div>
          <div>
            <span className="font-semibold">Constitution:</span>{" "}
            <span>{constitutionTypeLabel}</span>
          </div>
          <div>
            <span className="font-semibold">Authorised Signatory:</span>{" "}
            <span>{authorizedSignatory}</span>
          </div>
          <div>
            <span className="font-semibold">Auction ID:</span>{" "}
            <span>{auctionId}</span>
          </div>
          {process?.env?.NEXT_PUBLIC_ENABLE_OFFLINE_NI_KYC !== "true" && (
            <div>
              <span className="font-semibold">Event Name:</span>{" "}
              <span>{eventData?.event_name || "N/A"}</span>
            </div>
          )}
        </div>
      </div>

      {/* Declaration Section */}
      <div className="mb-6">
        <h3 className="text-sm font-bold mb-3">Declaration</h3>
        <div className="bg-orange-50 border border-orange-200 rounded-md p-4 mb-4">
          <p className="text-sm text-gray-700 leading-relaxed">
            {declarationText}
          </p>
        </div>

        {/* Confirmation Checkbox */}
        <div className="flex items-start gap-3">
          <input
            id="declaration-confirm"
            type="checkbox"
            checked={isChecked}
            onChange={() => setIsChecked(!isChecked)}
            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label
            htmlFor="declaration-confirm"
            className="text-sm text-gray-700 cursor-pointer"
          >
            I / We confirm that the details and documents provided are true and
            correct and that the above declaration is accepted.
          </label>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="mt-4 mb-10 md:mb-0">
        <Button
          className="w-full max-w-xl h-10 text-lg"
          onClick={handleSubmit}
          disabled={loading || !isChecked}
        >
          {loading ? "Authorizing..." : "Authorize"}
        </Button>
      </div>
    </div>
  );
}
